import Swal from "sweetalert2";
import PropTypes from 'prop-types';
import styled from 'styled-components';
import EditIcon from 'mdi-react/EditIcon';
import { Spinner } from 'react-bootstrap';
import DeleteIcon from 'mdi-react/DeleteIcon';
import Badge from '@/shared/components/Badge';
import React, { useState, useEffect } from 'react';
import AdminInterface from '@/utils/AdminInterface';
import withReactContent from "sweetalert2-react-content";
import { paddingLeft, paddingRight } from '@/utils/directions';
import ArrowUpBoldCircleIcon from 'mdi-react/ArrowUpBoldCircleIcon'
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import ImageFilterCenterFocusIcon from 'mdi-react/ImageFilterCenterFocusIcon';
import { colorAdditional, colorGreenHover, colorAccent } from '@/utils/palette';
import { Card, CardBody, CardTitleWrap, CardTitle } from '@/shared/components/Card';
import { CollectionCategoryEnum, CollectionCardinalDirectionEnum } from '../../../../../Enums';

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  svg:hover {
    fill: ${colorGreenHover};
  }

  .orange-hover:hover {
    fill: #e9c886;
  }

  .red-hover:hover {
    fill: #e98686;
  }
`;

const TableSpinner = styled(Spinner)`
  color: ${colorAccent};
`;

const StatusFormatter = value => (
    value ? <Badge bg="success">Enable</Badge> : <Badge bg="secondary">Disable</Badge>
);

StatusFormatter.propTypes = {
    value: PropTypes.bool.isRequired,
};

const CollectionsList = (props) => {
    const [showLoader, setShowLoader] = useState(true);
    const [collectionsList, setCollectionsList] = useState({ tableHeaderData: [], tableRowsData: [] });
    const tableConfig = {
        isEditable: false,
        isSortable: true,
        isResizable: false,
        withPagination: false,
        withSearchEngine: true,
        placeholder: 'Search...',
    };

    const handleEdit = (collection) => {
        props.setSelectedCollection(collection)
    }

    const handleDelete = (collection) => {
        Swal.fire({
            title: 'Warning!',
            text: `Do you want to delete the collection ${collection.name}?`,
            icon: 'warning',
            confirmButtonText: 'Yes!',
            cancelButtonText: 'No!',
            showCancelButton: true
        }).then((result) => {
            if (result.isConfirmed) {
                AdminInterface.instance.deleteCollection(collection.id).then(() => {
                    withReactContent(Swal).fire({
                        title: "Success",
                        html: "Collection is deleted!",
                    });
                    props.setListRequiresReload(true);
                }).catch((error) => {
                    withReactContent(Swal).fire({
                        title: "Error",
                        html: error.response?.data?.detail,
                    });
                })
            }
        })

    }

    const GetCollections = () => {
        setShowLoader(true);
        AdminInterface.instance.getCollections().then((response) => {
            const columns = [
                {
                    accessor: 'id',
                    Header: 'Id',
                },
                {
                    accessor: 'name',
                    Header: 'Name',
                },
                {
                    accessor: 'parent',
                    Header: 'Parent',
                },
                {
                    accessor: 'category',
                    Header: 'Category',
                },
                {
                    accessor: 'default_bucket_name',
                    Header: 'Default bucket name',
                },
                {
                    accessor: 'cardinal_direction',
                    Header: 'Cardinal direction',
                    disableGlobalFilter: true
                },
                {
                    accessor: 'is_demonstrable',
                    Header: 'Visible?',
                    disableGlobalFilter: true
                },
                {
                    accessor: 'is_exportable',
                    Header: 'Exportable?',
                    disableGlobalFilter: true
                },
                {
                    accessor: 'edit',
                    Header: 'Edit',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                },
                {
                    accessor: 'delete',
                    Header: 'Delete',
                    disableGlobalFilter: true,
                    disableSortBy: true,
                }
            ];
            const data = [];
            response.data.forEach(collection => {
                let cardinal_direction = collection.cardinal_direction === undefined ? undefined : Object.keys(CollectionCardinalDirectionEnum).find(direction => CollectionCardinalDirectionEnum[direction] === collection.cardinal_direction)
                let icon_rotation_angle = cardinal_direction == 'North' ? 0 : cardinal_direction == 'East' ? 90 : cardinal_direction == 'South' ? 180 : 270;
                data.push({
                    id: collection.id,
                    name: collection.name,
                    parent: collection.parent_id ? collection.parent.name : '',
                    category: collection.category === undefined ? '' : Object.keys(CollectionCategoryEnum).find(category => CollectionCategoryEnum[category] === collection.category),
                    default_bucket_name: collection.default_bucket_name === undefined ? '' : collection.default_bucket_name,
                    cardinal_direction: cardinal_direction === undefined ? '' : cardinal_direction === 'Nadir' ? <ImageFilterCenterFocusIcon /> : <ArrowUpBoldCircleIcon style = {{transform: `rotate(${icon_rotation_angle}deg)` }}/>,
                    is_exportable: StatusFormatter(collection.is_exportable),
                    is_demonstrable: StatusFormatter(collection.is_demonstrable),
                    edit: <CartTableButton type="button" onClick={() => handleEdit(collection)}><EditIcon className="orange-hover"/></CartTableButton>,
                    delete: <CartTableButton type="button" onClick={() => handleDelete(collection)}><DeleteIcon className="red-hover"/></CartTableButton>,
                });
            });

            setCollectionsList({ tableHeaderData: columns, tableRowsData: data });
        }).catch((error) => {
            withReactContent(Swal).fire({
                title: "Error",
                html: error.response?.data?.detail,
            });
        }).finally(() => {
            setShowLoader(false);
            props.setListRequiresReload(false);
        });
    }

    useEffect(() => {
        GetCollections();
    }, [props.listRequiresReload]);

    return (
        <Card>
            <CardBody>
                <CardTitleWrap>
                    <CardTitle>Collections List</CardTitle>
                </CardTitleWrap>
                {
                    !showLoader &&
                    <ReactTableBase columns={collectionsList.tableHeaderData} data={collectionsList.tableRowsData} tableConfig={tableConfig} />
                }
                {showLoader && (<div className="text-center"><TableSpinner animation="border" /></div>)}
            </CardBody>
        </Card>
    );
};

export default CollectionsList;